import React, { useContext, useEffect, useRef } from 'react';
import SwiperCore, { Navigation, Pagination, Thumbs } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ProductInfoContext } from 'utils/helpers/pdpContexts';

import * as S from './styles';

SwiperCore.use([Navigation, Pagination, Thumbs]);

type SliderImage = {
  image: string | React.ReactNode;
  imageAlt: string;
  id?: string;
};

type Props = {
  /** Images that will be shown on the slider */
  sliderImages: SliderImage[];
  showNewAboveTheFoldPdp?: boolean;
};

/**
 * Slider image component for PDP
 * @param Props
 * @returns JSX.Element
 */
const PDPSwiperWrapper = ({
  sliderImages,
  showNewAboveTheFoldPdp,
}: Props): JSX.Element => {
  // Using a variable to track the last touch event. The swipper does not accept setState call as callbacks.
  let lastTouchEndEvent: MouseEvent | TouchEvent | PointerEvent | undefined =
    undefined;
  const PREV_SLIDE_CLASS = 'prev-slide';
  const NEXT_SLIDE_CLASS = 'next-slide';
  const swiperRef = useRef<HTMLDivElement>(null);
  const productData = useContext(ProductInfoContext);

  const { name, productId } = productData;

  const pagination = {
    clickable: true,
    renderBullet: (_index: number, className: string) =>
      `<span class="${className}"></span>`,
  };

  const callSlideChangeEvent = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_SLIDE,
      data: { name, productId },
    });
  };

  const onRealIndexChange = () => {
    // If the last touch end is not undfined onSlideChangeFromSwipe
    if (lastTouchEndEvent) {
      callSlideChangeEvent();
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.HERO_SWIPE,
        data: { name, productId },
      });
    }
    // Reset last touch end event back to undefined
    lastTouchEndEvent = undefined;
  };

  const onTouchEnd = (
    _swiperCore: SwiperCore,
    event: MouseEvent | TouchEvent | PointerEvent,
  ) => {
    lastTouchEndEvent = event;
  };

  const onPrevClick = () => {
    callSlideChangeEvent();
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_PREV_ARROW_CLICK,
      data: { name, productId },
    });
  };

  const onNextClick = () => {
    callSlideChangeEvent();
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_NEXT_ARROW_CLICK,
      data: { name, productId },
    });
  };

  useEffect(() => {
    const onBulletClickEvent = (index: number) => {
      const bulletIndex = index + 1;
      ewAnalytics.track({
        event: ANALYTICS.EVENTS.HERO_BULLET_CLICK,
        data: { name, productId, bulletIndex },
      });
    };
    const swiperBullets = swiperRef?.current?.querySelectorAll(
      '.swiper-pagination-bullet',
    );

    if (swiperBullets) {
      swiperBullets.forEach((bullet: Element, index: number) =>
        bullet.addEventListener('click', () => onBulletClickEvent(index)),
      );
    }

    return () => {
      if (swiperBullets) {
        swiperBullets.forEach((bullet: Element, index: number) =>
          bullet.removeEventListener('click', () => onBulletClickEvent(index)),
        );
      }
    };
  }, [swiperRef, name, productId]);

  return (
    <>
      <S.MainContainer showNewAboveTheFoldPdp={showNewAboveTheFoldPdp}>
        <div ref={swiperRef}>
          <Swiper
            loop={sliderImages.length > 1}
            navigation={{
              prevEl: `.${PREV_SLIDE_CLASS}`,
              nextEl: `.${NEXT_SLIDE_CLASS}`,
            }}
            onRealIndexChange={onRealIndexChange}
            onTouchEnd={onTouchEnd}
            pagination={pagination}
            // Needed to avoid briefly showing the last slide while loading
            slidesPerView="auto"
          >
            {sliderImages.map(
              (sliderImage, index) =>
                typeof sliderImage.image === 'object' && (
                  <SwiperSlide key={`swiper-image-${sliderImage.id}`}>
                    {sliderImage.image}
                  </SwiperSlide>
                ),
            )}
          </Swiper>
        </div>
      </S.MainContainer>

      {/* Previous arrow */}
      <S.NavigationButton
        aria-label="Previous Slide"
        className={PREV_SLIDE_CLASS}
        onClick={onPrevClick}
        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
      >
        <S.LeftIcon />
      </S.NavigationButton>

      {/* Next arrow */}
      <S.NavigationButton
        aria-label="Next Slide"
        className={NEXT_SLIDE_CLASS}
        onClick={onNextClick}
        showNewAboveTheFoldPdp={showNewAboveTheFoldPdp || false}
      >
        <S.RightIcon />
      </S.NavigationButton>
    </>
  );
};

export default PDPSwiperWrapper;
